import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  AppShell,
  Button,
  ContextualToolbar,
  FieldActionsProvider,
  Header,
  Hint,
  Sidebar,
  SidebarToggleButton,
  useUiPreferences,
  type SidebarItem,
} from '@rammetec/design-system'
import { AiClientProvider, createAiClient, type AiClient } from '@rammetec/app-kit/ai'
import {
  AssistantPanel,
  AssistantProvider,
  useAssistant,
  useAssistantPanelPlacement,
} from '@rammetec/app-kit/assistant'
import {
  createNotificationsAssistantTools,
  createNotificationsClient,
  NotificationsProvider,
  useNotifications,
  type NotificationLink,
  type NotificationRecord,
  type NotificationsClient,
} from '@rammetec/app-kit/notifications'
import {
  DesktopNotifier,
  NotificationCenterButton,
} from '@rammetec/app-kit/notifications-web'
import { usePreference } from '@rammetec/app-kit/preferences'
import { rtLogService } from '@rammetec/core'
import { LayoutDashboard, LayoutList, Sparkles } from 'lucide-react'
import {
  useAvailableInstances,
  useAvailableModules,
  useCurrentOrganizationData,
  useCurrentOrgUserData,
  useCurrentUser,
  useRemoteCommands,
  useRepositoryLazy,
  useSessionManager,
} from '@syscolect/application'
import {
  assistantAutoApprovePref,
  NOTIFICATION_ENTITY,
  userService,
  type ModuleInstance,
} from '@syscolect/domain'
import { OrganizationWarningButton } from '@/shared/components/OrganizationWarningButton'
import { SessionSelectors } from '@/shared/components/SessionSelectors'
import { UserMenu, UserMenuFallback } from '@/shared/components/UserMenu'
import { authService } from '@/services/authService'
import { systemApiBaseUrl } from '@/services/systemApiClient'
import { useMonitorRemoteCommands } from '@/services/monitor-remote-commands'
import type { AppModule } from '@/app/modules/registry'
import { useInstanceDisplayMap, type InstanceDisplay } from '@/app/modules/InstanceDisplayContext'
import { formatAppVersionLabel } from '@/config/appInfo'
import { AppShortcuts } from './AppShortcuts'
import {
  createAssistantGlobalTools,
  globalAssistantCatalogKey,
  buildOpenableModuleOptions,
} from './assistantGlobalTools'
import { filterQueryableEntities } from './assistantEntityCatalog'
import { SC_ASSISTANT_SYSTEM_PROMPT } from './assistantPrompt'

const ASSISTANT_OPEN_PREF_KEY = 'sc.assistant.open'
const ASSISTANT_PANEL_WIDTH_KEY = 'sc.assistant.panel'
const SIDEBAR_COLLAPSED_PREF_KEY = 'sc.sidebar.collapsed'
/** Same breakpoint AppShell uses for its mobile default (below Tailwind `md`). */
const SIDEBAR_MOBILE_MEDIA_QUERY = '(max-width: 767px)'

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(SIDEBAR_MOBILE_MEDIA_QUERY).matches
}

/**
 * Sidebar open/closed persisted in the user preferences (desktop only). On
 * mobile the sidebar always starts collapsed and toggling it is not saved —
 * otherwise opening it once on the phone would reopen it on every desktop load,
 * and vice versa.
 */
function usePersistedSidebarCollapsed(): [boolean, (collapsed: boolean) => void] {
  const uiPreferences = useUiPreferences()
  const [collapsed, setCollapsed] = useState(() =>
    isMobileViewport() ? true : (uiPreferences.getBool(SIDEBAR_COLLAPSED_PREF_KEY) ?? false),
  )
  const setCollapsedPersisted = useCallback(
    (next: boolean) => {
      setCollapsed(next)
      if (!isMobileViewport()) uiPreferences.setBool(SIDEBAR_COLLAPSED_PREF_KEY, next)
    },
    [uiPreferences],
  )
  return [collapsed, setCollapsedPersisted]
}

/** `systemApiBaseUrl` throws without a host — warn once, never crash the shell. */
let warnedMissingAiHost = false
let warnedMissingNotificationsHost = false

function buildSidebarItems(
  modules: readonly AppModule[],
  instances: readonly ModuleInstance[],
  instanceDisplay: ReadonlyMap<string, InstanceDisplay>,
): SidebarItem[] {
  const modBySlug = new Map(modules.map((m) => [m.slug, m]))

  const build = (parentMid?: string): SidebarItem[] =>
    instances
      .filter((i) => i.parentMid === parentMid)
      .map((inst) => {
        const def = modBySlug.get(inst.slug)
        if (def?.kind === 'group') {
          return {
            kind: 'group' as const,
            id: inst.id,
            label: inst.label ?? def.label,
            icon: instanceDisplay.get(inst.id)?.icon ?? def?.iconNode,
            defaultOpen: false,
            items: build(inst.id),
          }
        }
        return {
          kind: 'item' as const,
          id: inst.id,
          label: inst.label ?? def?.label ?? inst.slug,
          href: `/m/${inst.slug}?mid=${inst.id}`,
          icon: def?.iconNode,
        }
      })

  return [
    {
      kind: 'item',
      id: 'workspace',
      label: 'Área de trabalho',
      href: '/',
      icon: <LayoutDashboard />,
      shortcut: 'Ctrl+Alt+D',
    },
    {
      kind: 'group',
      id: 'modules',
      label: 'Módulos',
      defaultOpen: true,
      icon: <LayoutList />,
      items: build(undefined),
    },
  ]
}

function getActiveItem(pathname: string, mid: string | null) {
  if (pathname.startsWith('/m/')) return mid ?? undefined
  if (pathname === '/' || pathname === '') return 'workspace'
  return undefined
}

export function AppLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { isLoading, currentUserId } = useCurrentUser()
  const instances = useAvailableInstances()
  const modules = useAvailableModules<AppModule>()
  const instanceDisplay = useInstanceDisplayMap()
  const { currentOrganizationId } = useSessionManager()
  const { organizationData } = useCurrentOrganizationData()
  const { orgUserData } = useCurrentOrgUserData()
  const uiPreferences = useUiPreferences()
  useRemoteCommands()
  useMonitorRemoteCommands()

  // Syscolect's useCurrentUser has no `user`; the AuthUser comes from authService.
  const authUser = authService.getCurrentUser()

  // Org/profile selectors are hidden inside module routes to free header space.
  const isModuleRoute = location.pathname.startsWith('/m/')

  const aiContracted = organizationData?.products?.ai?.contracted === true

  // Per-user flag on user/{uid}/orgs/{orgId} — absent = enabled (request 0308).
  const { data: aiUserAllowed = true } = useQuery({
    queryKey: ['user-org-ai-assistant', authUser?.userId, currentOrganizationId],
    queryFn: async () => {
      if (!authUser?.userId || !currentOrganizationId) return true
      const link = await userService.getUserOrganization(
        authUser.userId,
        currentOrganizationId,
      )
      return link?.aiAssistantEnabled !== false
    },
    enabled: !!authUser?.userId && !!currentOrganizationId && aiContracted,
  })

  const [assistantOpen, setAssistantOpen] = useState(
    () => uiPreferences.getBool(ASSISTANT_OPEN_PREF_KEY) ?? false,
  )

  const setAssistantOpenPersisted = (open: boolean) => {
    setAssistantOpen(open)
    uiPreferences.setBool(ASSISTANT_OPEN_PREF_KEY, open)
  }

  /** Tool names the user opted to auto-approve ("Sempre aplicar") — user scope. */
  const [autoApprovedTools, setAutoApprovedTools] = usePreference(assistantAutoApprovePref)

  const orgIdRef = useRef(currentOrganizationId)
  orgIdRef.current = currentOrganizationId

  const orgRole = orgUserData?.orgUserData?.role
  const isOrgAdminRef = useRef(false)
  isOrgAdminRef.current = orgRole === 'admin' || orgRole === 'system'

  /** Filled by {@link AssistantAuditReasonBridge} inside AssistantProvider. */
  const auditCtxRef = useRef<{ conversationId: string | null }>({ conversationId: null })

  /** Filled by {@link NotificationsAssistantBridge} inside NotificationsProvider. */
  const notificationsBridgeRef = useRef<{
    unread: NotificationRecord[]
    open: (n: NotificationRecord) => Promise<void>
  }>({ unread: [], open: async () => {} })

  const locationRef = useRef({
    pathname: location.pathname,
    mid: searchParams.get('mid'),
    modules,
    instances,
  })
  locationRef.current = {
    pathname: location.pathname,
    mid: searchParams.get('mid'),
    modules,
    instances,
  }

  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  const {
    getRepository: getNotificationRepository,
    isReady: notificationRepoReady,
  } = useRepositoryLazy<NotificationRecord>(NOTIFICATION_ENTITY.notification)

  // Fingerprint of leaf labels + queryable entities — array identity churn must
  // not rebuild tools (invalidates the provider tool-prefix cache on every render).
  const catalogKey = useMemo(
    () => globalAssistantCatalogKey(instances, modules),
    [instances, modules],
  )

  // try/catch is intentional: `systemApiBaseUrl` throws when neither
  // VITE_SYSTEM_API_HOST nor Global Config `systemApiHost` is set. Calling it
  // during AppLayout render would white-screen the whole console (every route),
  // including orgs that never contracted AI. Same lazy-host idea as addressApi —
  // missing host means "no assistant", not "no console". Do not "clean" this away.
  const aiClient = useMemo((): AiClient | null => {
    try {
      return createAiClient({
        baseUrl: systemApiBaseUrl(''),
        getIdToken: () => authService.getIdToken(),
        getOrganizationId: async () => orgIdRef.current ?? null,
      })
    } catch {
      if (!warnedMissingAiHost) {
        warnedMissingAiHost = true
        rtLogService.warn(
          'Assistente de IA indisponível: host do system-api não configurado (VITE_SYSTEM_API_HOST / systemApiHost).',
        )
      }
      return null
    }
  }, [])

  // Same host-guard pattern as aiClient — missing system-api host means no
  // notifications UI, never a white-screened console (request 0304).
  const notificationsClient = useMemo((): NotificationsClient | null => {
    try {
      return createNotificationsClient({
        baseUrl: systemApiBaseUrl(''),
        getIdToken: () => authService.getIdToken(),
        getOrganizationId: async () => orgIdRef.current ?? null,
      })
    } catch {
      if (!warnedMissingNotificationsHost) {
        warnedMissingNotificationsHost = true
        rtLogService.warn(
          'Notificações indisponíveis: host do system-api não configurado (VITE_SYSTEM_API_HOST / systemApiHost).',
        )
      }
      return null
    }
  }, [])

  const handleNotificationNavigate = useCallback((link: NotificationLink) => {
    const params = new URLSearchParams(link.params ?? {})
    if (link.recordId) params.set('id', link.recordId)
    const qs = params.toString()
    // Same rule as slugFromPath — covers notifications already stored with hyphenated module.
    const module = link.module.replace(/-/g, '')
    navigateRef.current(`/m/${module}${qs ? `?${qs}` : ''}`)
  }, [])

  const globalTools = useMemo(() => {
    const snap = locationRef.current
    const openable = buildOpenableModuleOptions(snap.instances, snap.modules)
    const queryable = filterQueryableEntities(snap.instances)
    const tools = createAssistantGlobalTools(
      () => locationRef.current,
      (to) => navigateRef.current(to),
      openable,
      queryable,
      {
        getOrgId: () => orgIdRef.current ?? null,
        getAuditReason: () => {
          const id = auditCtxRef.current.conversationId
          return id ? `conversa ${id}` : 'inclusão pelo assistente'
        },
      },
      {
        getOrgId: () => orgIdRef.current ?? null,
        isOrgAdmin: () => isOrgAdminRef.current,
      },
    )
    if (!notificationsClient) return tools
    return [
      ...tools,
      ...createNotificationsAssistantTools({
        getUnread: () => notificationsBridgeRef.current.unread,
        open: (n) => notificationsBridgeRef.current.open(n),
      }),
    ]
    // catalogKey is the content fingerprint; locationRef / bridge refs hold latest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogKey])

  const assistantAvailable = aiClient != null
  const notificationsAvailable = notificationsClient != null
  /** Mount provider + button only when org contracted AND user allowed (0308). */
  const mountAssistant = assistantAvailable && aiContracted && aiUserAllowed

  const shell = (
    <AppLayoutShell
      assistantAvailable={mountAssistant}
      notificationsAvailable={notificationsAvailable}
      aiContracted={aiContracted}
      assistantOpen={assistantOpen}
      onAssistantOpenChange={setAssistantOpenPersisted}
      authUser={authUser}
      isLoading={isLoading}
      isModuleRoute={isModuleRoute}
      modules={modules}
      instances={instances}
      instanceDisplay={instanceDisplay}
      pathname={location.pathname}
      mid={searchParams.get('mid')}
      onNavigate={navigate}
    />
  )

  let tree: ReactNode = shell
  if (mountAssistant && aiClient) {
    tree = (
      <AiClientProvider client={aiClient}>
        <AssistantProvider
          client={aiClient}
          usePoint="sc.assistant"
          persistConversation
          // 30, nao 10: "inclua um produto baseado no 8008" gastou exatamente 10
          // (achar o modelo, abrir o dialogo, resolver tres lookups) e morreu no
          // setField, com o formulario aberto e vazio. O teto e rede contra laco,
          // nao orcamento - quem controla custo e ROUNDS_PER_TOKEN no servidor.
          maxRounds={30}
          systemPrompt={SC_ASSISTANT_SYSTEM_PROMPT}
          globalTools={globalTools}
          autoApprovedTools={autoApprovedTools}
          onAutoApprovedToolsChange={(next) => setAutoApprovedTools([...next])}
        >
          <AssistantAuditReasonBridge target={auditCtxRef.current} />
          {tree}
        </AssistantProvider>
      </AiClientProvider>
    )
  }
  if (notificationsClient) {
    tree = (
      <NotificationsProvider
        client={notificationsClient}
        userId={authUser?.userId ?? null}
        orgId={notificationRepoReady ? (currentOrganizationId ?? null) : null}
        getRepository={getNotificationRepository}
        onNavigate={handleNotificationNavigate}
      >
        <NotificationsAssistantBridge target={notificationsBridgeRef.current} />
        {/* Same gate as PreferencesBridge — usePreference throws without PreferencesProvider. */}
        {currentUserId ? <DesktopNotifier iconUrl="/favicon.svg" /> : null}
        {tree}
      </NotificationsProvider>
    )
  }

  return <FieldActionsProvider>{tree}</FieldActionsProvider>
}

/**
 * Keeps the conversation id available to global write tools without rebuilding
 * the tool array (provider owns the id; tools were created in the parent).
 */
function AssistantAuditReasonBridge({
  target,
}: {
  target: { conversationId: string | null }
}) {
  const { conversationId } = useAssistant()
  target.conversationId = conversationId
  return null
}

/**
 * Keeps live unread + open available to assistant tools without rebuilding the
 * tool array (NotificationsProvider is below AppLayout's useMemo).
 */
function NotificationsAssistantBridge({
  target,
}: {
  target: {
    unread: NotificationRecord[]
    open: (n: NotificationRecord) => Promise<void>
  }
}) {
  const { unread, open } = useNotifications()
  target.unread = unread
  target.open = open
  return null
}

/** Shell body — reads panel placement inside AssistantProvider when present. */
function AppLayoutShell({
  assistantAvailable,
  notificationsAvailable,
  aiContracted,
  assistantOpen,
  onAssistantOpenChange,
  authUser,
  isLoading,
  isModuleRoute,
  modules,
  instances,
  instanceDisplay,
  pathname,
  mid,
  onNavigate,
}: {
  assistantAvailable: boolean
  notificationsAvailable: boolean
  aiContracted: boolean
  assistantOpen: boolean
  onAssistantOpenChange: (open: boolean) => void
  authUser: ReturnType<typeof authService.getCurrentUser>
  isLoading: boolean
  isModuleRoute: boolean
  modules: readonly AppModule[]
  instances: readonly ModuleInstance[]
  instanceDisplay: ReadonlyMap<string, InstanceDisplay>
  pathname: string
  mid: string | null
  onNavigate: (to: string) => void
}) {
  // Outside provider → 'shell'; with a dialog host → only dialog mounts the panel.
  const panelPlacement = useAssistantPanelPlacement()
  const shellHostsPanel = panelPlacement === 'shell'
  const [sidebarCollapsed, setSidebarCollapsed] = usePersistedSidebarCollapsed()
  const sidePanelOpen =
    assistantAvailable && aiContracted && assistantOpen && shellHostsPanel

  return (
    <>
      <AppShortcuts />
      <AppShell
        sidebarCollapsed={sidebarCollapsed}
        onSidebarCollapsedChange={setSidebarCollapsed}
        header={
          <Header
            brand={{ id: 'syscolect', label: 'SysColect' }}
            logo={<img src="/favicon.svg" alt="SysColect" className="h-5 w-5" />}
            startSlot={<SidebarToggleButton />}
            centerSlot={
              <ContextualToolbar overflow align="end" className="min-w-0 flex-1" />
            }
            endSlot={
              authUser ? (
                <div className="flex items-center gap-2">
                  {!isModuleRoute && <SessionSelectors />}
                  <OrganizationWarningButton />
                  {notificationsAvailable && (
                    <NotificationCenterButton
                      onViewAll={() => onNavigate('/notifications')}
                      onOpenPreferences={() =>
                        onNavigate('/notifications#preferencias')
                      }
                    />
                  )}
                  {assistantAvailable && aiContracted && (
                    <Hint label="Assistente de IA" side="bottom">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Assistente de IA"
                        aria-pressed={assistantOpen}
                        onClick={() => onAssistantOpenChange(!assistantOpen)}
                      >
                        <Sparkles />
                      </Button>
                    </Hint>
                  )}
                  <UserMenu user={authUser} />
                </div>
              ) : isLoading ? (
                <UserMenuFallback />
              ) : null
            }
          />
        }
        sidebar={
          <Sidebar
            searchable
            searchShortcut="Ctrl+Alt+S"
            items={buildSidebarItems(modules, instances, instanceDisplay)}
            activeId={getActiveItem(pathname, mid)}
            onItemClick={(_, item, e) => {
              if (item.kind !== 'item' || !item.href) return
              // Let Ctrl/Cmd/Shift/Alt + middle-click fall through to native new-tab.
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return
              e.preventDefault()
              onNavigate(item.href)
            }}
            footer={
              <div className="px-2 py-1 font-mono text-xs text-fg-muted">
                SysColect - Console <br />
                Ramme Tech © {new Date().getFullYear()} <br />
                {formatAppVersionLabel()}
              </div>
            }
          />
        }
        sidePanel={
          shellHostsPanel && assistantAvailable && authUser ? (
            <AssistantPanel
              currentUserId={authUser.userId}
              attachments
              camera
              onCollapse={() => onAssistantOpenChange(false)}
            />
          ) : undefined
        }
        sidePanelOpen={sidePanelOpen}
        sidePanelWidthKey={ASSISTANT_PANEL_WIDTH_KEY}
      >
        <Outlet />
      </AppShell>
    </>
  )
}
